import { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { catalogApi } from '../api/catalog';
import { contentApi } from '../api/content';
import type { CategoryDto, ProductQueryDto } from '../api/types';
import { ProductCard, ProductGridSkeleton } from '../components/ProductCard';
import { Seo } from '../components/Seo';
import { EmptyState, ErrorState, Pagination } from '../components/ui';
import { money } from '../lib/format';
import { trackSearch } from '../lib/analytics';

/* ------------------------------------------------------------------ *
 *  Urun listesi.
 *
 *  Tum filtreler URL'de tutulur: sayfa paylasilabilir, geri tusu calisir
 *  ve yenilemede filtre kaybolmaz.
 * ------------------------------------------------------------------ */

const SORT_OPTIONS = [
  { value: '', label: 'Önerilen' },
  { value: 'price', label: 'En düşük fiyat' },
  { value: 'price:desc', label: 'En yüksek fiyat' },
  { value: 'newest', label: 'En yeni' },
  { value: 'bestselling', label: 'En çok satan' },
  { value: 'reviews', label: 'En çok yorumlanan' },
  { value: 'rating', label: 'En yüksek puan' },
  { value: 'discount', label: 'İndirim oranı' },
];

const COLOR_MAP: Record<string, string> = {
  siyah: '#000000',
  black: '#000000',
  beyaz: '#ffffff',
  white: '#ffffff',
  kırmızı: '#ef4444',
  kirmizi: '#ef4444',
  red: '#ef4444',
  mavi: '#3b82f6',
  blue: '#3b82f6',
  yeşil: '#10b981',
  yesil: '#10b981',
  green: '#10b981',
  sarı: '#eab308',
  sari: '#eab308',
  yellow: '#eab308',
  turuncu: '#f97316',
  orange: '#f97316',
  mor: '#a855f7',
  purple: '#a855f7',
  pembe: '#ec4899',
  pink: '#ec4899',
  gri: '#6b7280',
  gray: '#6b7280',
  grey: '#6b7280',
  lacivert: '#1e3a8a',
  navy: '#1e3a8a',
  bordo: '#831843',
  haki: '#4b5320',
  kahverengi: '#78350f',
  brown: '#78350f',
  bej: '#f5f5dc',
  beige: '#f5f5dc',
  antrasit: '#334155',
  altın: '#d4af37',
  gümüş: '#c0c0c0',
};

function parseFacetColor(item: { value: string; label?: string | null; colorCode?: string | null }): {
  cleanLabel: string;
  colorHex: string;
} {
  const raw = item.label || item.value;
  let cleanLabel = raw;
  let colorHex = item.colorCode || '';

  if (raw.includes('|#')) {
    const parts = raw.split('|#');
    cleanLabel = parts[0].trim();
    colorHex = `#${parts[1].trim()}`;
  } else if (raw.includes('|')) {
    const parts = raw.split('|');
    cleanLabel = parts[0].trim();
    colorHex = parts[1].trim();
  }

  if (!colorHex || !colorHex.startsWith('#')) {
    const lower = cleanLabel.toLowerCase().trim();
    if (COLOR_MAP[lower]) {
      colorHex = COLOR_MAP[lower];
    } else {
      for (const [key, hex] of Object.entries(COLOR_MAP)) {
        if (lower.includes(key)) {
          colorHex = hex;
          break;
        }
      }
    }
  }

  if (!colorHex) {
    colorHex = '#94a3b8';
  }

  return { cleanLabel, colorHex };
}

export interface ProductListPageProps {
  /** Kategori / marka / koleksiyon sayfalarindan gelen sabit filtre. */
  fixedFilter?: Partial<ProductQueryDto>;
  title?: string;
  description?: string | null;
  subCategories?: CategoryDto[];
}

export function ProductListPage({ fixedFilter, title, description, subCategories }: ProductListPageProps) {
  const [params, setParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const page = Number(params.get('page') ?? 1);
  const sortParam = params.get('sort') ?? '';
  const [sortBy, sortDirection] = sortParam.split(':');

  const optionFilters = useMemo(() => {
    const result: Record<string, string[]> = {};
    params.forEach((value, key) => {
      if (key.startsWith('o.')) {
        const name = key.slice(2);
        (result[name] ??= []).push(value);
      }
    });
    return result;
  }, [params]);

  const query = useMemo<ProductQueryDto>(
    () => ({
      page,
      pageSize: 24,
      search: params.get('search') ?? undefined,
      categoryIds: params.getAll('category').map(Number).filter(Boolean),
      brandIds: params.getAll('brand').map(Number).filter(Boolean),
      minPrice: params.get('min') ? Number(params.get('min')) : undefined,
      maxPrice: params.get('max') ? Number(params.get('max')) : undefined,
      inStockOnly: params.get('stock') === '1' ? true : undefined,
      onSaleOnly: params.get('sale') === '1' ? true : undefined,
      featuredOnly: params.get('featured') === '1' ? true : undefined,
      minRating: params.get('rating') ? Number(params.get('rating')) : undefined,
      tags: params.getAll('tag'),
      optionFilters,
      sortBy: sortBy || undefined,
      sortDescending: sortDirection === 'desc',
      ...fixedFilter,
    }),
    [params, page, sortBy, sortDirection, optionFilters, fixedFilter],
  );

  const products = useQuery({
    queryKey: ['products', 'search', query],
    queryFn: () => catalogApi.searchProducts(query),
    placeholderData: keepPreviousData,
  });

  // Analytics: Search event
  useEffect(() => {
    if (query.search && query.search.trim().length > 0) {
      trackSearch(query.search.trim());
    }
  }, [query.search]);

  // Facet sayimlari sayfa numarasindan bagimsizdir.
  const facets = useQuery({
    queryKey: ['products', 'facets', { ...query, page: 1 }],
    queryFn: () => catalogApi.productFacets({ ...query, page: 1 }),
    placeholderData: keepPreviousData,
  });

  const { data: categoryBanners } = useQuery({
    queryKey: ['content', 'banners', 'category'],
    queryFn: () => contentApi.banners('category'),
    staleTime: 30_000,
  });

  const { data: sidebarBanners } = useQuery({
    queryKey: ['content', 'banners', 'sidebar'],
    queryFn: () => contentApi.banners('sidebar'),
    staleTime: 30_000,
  });

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      next.delete('page'); // Filtre degisince ilk sayfaya donulur.
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const toggleMulti = (key: string, value: string) =>
    update((next) => {
      const existing = next.getAll(key);
      next.delete(key);
      const updated = existing.includes(value)
        ? existing.filter((item) => item !== value)
        : [...existing, value];
      updated.forEach((item) => next.append(key, item));
    });

  const setSingle = (key: string, value: string | null) =>
    update((next) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });

  const activeCount =
    params.getAll('category').length +
    params.getAll('brand').length +
    Object.values(optionFilters).flat().length +
    (params.get('min') ? 1 : 0) +
    (params.get('max') ? 1 : 0) +
    (params.get('stock') ? 1 : 0) +
    (params.get('sale') ? 1 : 0) +
    (params.get('rating') ? 1 : 0);

  const clearAll = () => {
    const next = new URLSearchParams();
    const search = params.get('search');
    if (search) next.set('search', search);
    setParams(next);
  };

  const filterPanel = (
    <div className="space-y-6">
      {activeCount > 0 ? (
        <button type="button" onClick={clearAll} className="btn-secondary btn-sm w-full">
          Filtreleri temizle ({activeCount})
        </button>
      ) : null}

      {/* 0. Alt Kategoriler Ağacı (Kategori sayfasındaysa) */}
      {subCategories && subCategories.length > 0 ? (
        <FilterGroup title="Alt Kategoriler">
          <ul className="space-y-1">
            {subCategories.map((sub) => (
              <li key={sub.id}>
                <Link
                  to={`/kategori/${sub.slug}`}
                  className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-brand-50 hover:text-brand-700"
                >
                  <span>{sub.name}</span>
                  {typeof sub.productCount === 'number' && sub.productCount > 0 ? (
                    <span className="text-xs text-ink-400">({sub.productCount})</span>
                  ) : null}
                </Link>
                {sub.children && sub.children.length > 0 ? (
                  <ul className="ml-3 border-l border-ink-100 pl-2.5 mt-0.5 space-y-0.5">
                    {sub.children.map((grand) => (
                      <li key={grand.id}>
                        <Link
                          to={`/kategori/${grand.slug}`}
                          className="flex items-center justify-between rounded-lg px-2 py-1 text-xs text-ink-600 transition hover:bg-brand-50 hover:text-brand-700"
                        >
                          <span>└ {grand.name}</span>
                          {typeof grand.productCount === 'number' && grand.productCount > 0 ? (
                            <span className="text-[11px] text-ink-400">({grand.productCount})</span>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </FilterGroup>
      ) : null}

      {/* 1. Kategori Filtresi */}
      {!fixedFilter?.categorySlug && facets.data && facets.data.categories.length > 0 ? (
        <FilterGroup title="Kategori">
          <ul className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
            {facets.data.categories.map((cat) => {
              const isChecked = params.getAll('category').includes(String(cat.id ?? cat.value));
              return (
                <li key={cat.value}>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 hover:text-ink-900">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                      checked={isChecked}
                      onChange={() => toggleMulti('category', String(cat.id ?? cat.value))}
                    />
                    <span className="flex-1 truncate">{cat.label ?? cat.value}</span>
                    <span className="text-xs text-ink-400">{cat.count}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </FilterGroup>
      ) : null}

      {/* 2. Fiyat Aralığı */}
      <FilterGroup title="Fiyat aralığı">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            placeholder={facets.data ? String(Math.floor(facets.data.minPrice)) : 'En az'}
            defaultValue={params.get('min') ?? ''}
            onBlur={(event) => setSingle('min', event.target.value || null)}
            className="field py-1.5 text-sm"
            aria-label="En düşük fiyat"
          />
          <span className="text-ink-400">–</span>
          <input
            type="number"
            min={0}
            placeholder={facets.data ? String(Math.ceil(facets.data.maxPrice)) : 'En çok'}
            defaultValue={params.get('max') ?? ''}
            onBlur={(event) => setSingle('max', event.target.value || null)}
            className="field py-1.5 text-sm"
            aria-label="En yüksek fiyat"
          />
        </div>
        {facets.data ? (
          <p className="hint">
            Bu listede {money(facets.data.minPrice)} – {money(facets.data.maxPrice)} arası
          </p>
        ) : null}
      </FilterGroup>

      {/* 3. Marka Filtresi */}
      {!fixedFilter?.brandSlug && facets.data && facets.data.brands.length > 0 ? (
        <FilterGroup title="Marka">
          <ul className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
            {facets.data.brands.map((brand) => (
              <li key={brand.value}>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 hover:text-ink-900">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    checked={params.getAll('brand').includes(String(brand.id ?? brand.value))}
                    onChange={() => toggleMulti('brand', String(brand.id ?? brand.value))}
                  />
                  <span className="flex-1 truncate">{brand.label ?? brand.value}</span>
                  <span className="text-xs text-ink-400">{brand.count}</span>
                </label>
              </li>
            ))}
          </ul>
        </FilterGroup>
      ) : null}

      {/* 4. Dinamik Özellikler (Attribute / Renk, Beden, vb.) */}
      {facets.data
        ? Object.entries(facets.data.options).map(([name, values]) => {
            const isColor = name.toLowerCase().includes('renk') || name.toLowerCase().includes('color');

            return (
              <FilterGroup key={name} title={name}>
                <ul className="max-h-56 space-y-2 overflow-y-auto pr-1">
                  {values.map((item) => {
                    const isChecked = (optionFilters[name] ?? []).includes(item.value);

                    if (isColor) {
                      const { cleanLabel, colorHex } = parseFacetColor(item);
                      return (
                        <li key={item.value}>
                          <label className="group flex cursor-pointer items-center gap-2.5 text-sm text-ink-700 hover:text-ink-900 transition">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                              checked={isChecked}
                              onChange={() => toggleMulti(`o.${name}`, item.value)}
                            />
                            {/* Renk yuvarlak top şeklinde gözükür */}
                            <span
                              className="h-4 w-4 rounded-full border border-black/20 shadow-sm shrink-0 transition-transform group-hover:scale-110"
                              style={{ backgroundColor: colorHex }}
                              title={cleanLabel}
                              aria-hidden="true"
                            />
                            {/* Renk kodu yazmaz, doğrudan renk adı yazar */}
                            <span className="flex-1 truncate font-medium text-ink-800">{cleanLabel}</span>
                            <span className="text-xs text-ink-400">{item.count}</span>
                          </label>
                        </li>
                      );
                    }

                    return (
                      <li key={item.value}>
                        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 hover:text-ink-900">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                            checked={isChecked}
                            onChange={() => toggleMulti(`o.${name}`, item.value)}
                          />
                          <span className="flex-1 truncate">{item.label ?? item.value}</span>
                          <span className="text-xs text-ink-400">{item.count}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </FilterGroup>
            );
          })
        : null}

      {/* 5. Stok Durumu ve İndirim */}
      <FilterGroup title="Stok ve İndirim">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 hover:text-ink-900">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
            checked={params.get('stock') === '1'}
            onChange={(event) => setSingle('stock', event.target.checked ? '1' : null)}
          />
          Yalnızca stokta olanlar
        </label>
        <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-ink-700 hover:text-ink-900">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
            checked={params.get('sale') === '1'}
            onChange={(event) => setSingle('sale', event.target.checked ? '1' : null)}
          />
          İndirimdekiler
        </label>
      </FilterGroup>

      {/* 6. Rating (Değerlendirme Puanı) */}
      <FilterGroup title="Değerlendirme Puanı">
        <div className="space-y-1.5">
          {[4, 3, 2].map((minStar) => {
            const isSelected = params.get('rating') === String(minStar);
            return (
              <button
                key={minStar}
                type="button"
                onClick={() => setSingle('rating', isSelected ? null : String(minStar))}
                className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm transition ${
                  isSelected
                    ? 'bg-amber-50 font-medium text-amber-900 ring-1 ring-amber-300'
                    : 'text-ink-700 hover:bg-ink-50'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center text-amber-400">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <svg
                        key={s}
                        viewBox="0 0 20 20"
                        className={`h-4 w-4 ${s <= minStar ? 'text-amber-400 fill-current' : 'text-ink-200 fill-current'}`}
                        aria-hidden="true"
                      >
                        <path d="M10 1.8l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 14.1l-4.8 2.6.9-5.4L2.2 7.5l5.4-.8L10 1.8z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-xs font-normal text-ink-600">ve üzeri</span>
                </div>
                {isSelected ? (
                  <span className="text-xs font-semibold text-amber-700">Seçili</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </FilterGroup>
    </div>
  );

  return (
    <div className="container-page py-8">
      {!fixedFilter?.categorySlug && !fixedFilter?.brandSlug && !fixedFilter?.collectionSlug ? (
        <Seo
          title={
            params.get('search')
              ? `"${params.get('search')}" Arama Sonuçları`
              : params.get('featured') === '1'
                ? 'Öne Çıkan Ürünler'
                : 'Tüm Ürünler'
          }
          description={
            params.get('search')
              ? `"${params.get('search')}" araması için en uygun ürün modelleri ve fiyatları.`
              : 'En yeni teknoloji, moda, giyim ve ev ürünlerini avantajlı fiyatlar ve hızlı kargo imkanıyla inceleyin.'
          }
        />
      ) : null}
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">
          {title ?? (params.get('search') ? `"${params.get('search')}" için sonuçlar` : 'Tüm ürünler')}
        </h1>
        {description ? (
          <div
            className="prose-content mt-2 max-w-3xl text-sm text-ink-600"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
          />
        ) : null}
      </header>

      {/* Kategori Sayfası Bannerı */}
      {categoryBanners && categoryBanners.length > 0 ? (
        <div className="mb-6 overflow-hidden rounded-2xl border border-ink-200 shadow-sm">
          {categoryBanners.slice(0, 1).map((b) => {
            const isExternal = b.linkUrl ? /^https?:\/\//i.test(b.linkUrl) : false;
            const content = (
              <div className="relative aspect-[21/6] min-h-[140px] w-full overflow-hidden bg-ink-100 sm:min-h-[180px]">
                <img
                  src={b.imageUrl}
                  alt={b.altText || b.title}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent flex items-center p-6 sm:p-10">
                  <div className="max-w-md text-white">
                    <h2 className="text-xl sm:text-2xl font-bold">{b.title}</h2>
                    {b.subtitle ? <p className="mt-1 text-xs sm:text-sm text-white/85">{b.subtitle}</p> : null}
                    {b.buttonText ? (
                      <span className="mt-3 inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-ink-900 shadow transition hover:bg-brand-50">
                        {b.buttonText} →
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            );

            return b.linkUrl ? (
              isExternal ? (
                <a key={b.id} href={b.linkUrl} target="_blank" rel="noreferrer noopener" className="block">
                  {content}
                </a>
              ) : (
                <Link key={b.id} to={b.linkUrl} className="block">
                  {content}
                </Link>
              )
            ) : (
              <div key={b.id}>{content}</div>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 border-y border-ink-200 py-3">
        <button
          type="button"
          className="btn-secondary btn-sm lg:hidden"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          Filtreler{activeCount > 0 ? ` (${activeCount})` : ''}
        </button>

        <p className="text-sm text-ink-500">
          {products.data ? `${products.data.totalCount} ürün` : 'Yükleniyor…'}
        </p>

        <div className="flex items-center gap-2">
          <label className="text-sm text-ink-500" htmlFor="sort">
            Sırala
          </label>
          <select
            id="sort"
            className="field w-auto py-1.5 text-sm"
            value={sortParam}
            onChange={(event) => setSingle('sort', event.target.value || null)}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-8 pt-6 lg:grid-cols-[260px_1fr]">
        <aside className={`${filtersOpen ? 'block' : 'hidden'} lg:block space-y-6`} aria-label="Filtreler">
          {filterPanel}

          {/* Yan Panel (Sidebar) Bannerları */}
          {sidebarBanners && sidebarBanners.length > 0 ? (
            <div className="space-y-4 pt-2">
              {sidebarBanners.slice(0, 2).map((b) => {
                const isExternal = b.linkUrl ? /^https?:\/\//i.test(b.linkUrl) : false;
                const card = (
                  <div className="group overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm transition hover:shadow-md">
                    <div className="aspect-[4/3] w-full overflow-hidden bg-ink-100">
                      <img
                        src={b.imageUrl}
                        alt={b.altText || b.title}
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-3.5">
                      <p className="text-sm font-semibold text-ink-900 group-hover:text-brand-600 transition">{b.title}</p>
                      {b.subtitle ? <p className="mt-0.5 text-xs text-ink-500">{b.subtitle}</p> : null}
                      {b.buttonText ? (
                        <span className="mt-2.5 inline-flex items-center text-xs font-medium text-brand-700">
                          {b.buttonText} →
                        </span>
                      ) : null}
                    </div>
                  </div>
                );

                return b.linkUrl ? (
                  isExternal ? (
                    <a key={b.id} href={b.linkUrl} target="_blank" rel="noreferrer noopener" className="block">
                      {card}
                    </a>
                  ) : (
                    <Link key={b.id} to={b.linkUrl} className="block">
                      {card}
                    </Link>
                  )
                ) : (
                  <div key={b.id}>{card}</div>
                );
              })}
            </div>
          ) : null}
        </aside>

        <div>
          {products.isError ? (
            <ErrorState error={products.error} onRetry={() => void products.refetch()} />
          ) : products.isLoading ? (
            <ProductGridSkeleton count={12} />
          ) : products.data && products.data.items.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {products.data.items.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              <Pagination
                page={products.data.page}
                totalPages={products.data.totalPages}
                totalCount={products.data.totalCount}
                onChange={(next) =>
                  setParams((current) => {
                    const updated = new URLSearchParams(current);
                    updated.set('page', String(next));
                    return updated;
                  })
                }
              />
            </>
          ) : (
            <EmptyState
              title={activeCount > 0 ? 'Filtrelere uygun ürün bulunamadı' : 'Henüz ürün eklenmedi'}
              description={
                activeCount > 0
                  ? 'Filtreleri gevşetmeyi veya farklı bir arama terimi denemeyi deneyin.'
                  : "Hurmahane'de yeni mahsul hurmalar ve hac malzemeleri çok yakında listelenecektir."
              }
              action={
                activeCount > 0 ? (
                  <button type="button" onClick={clearAll} className="btn-secondary">
                    Filtreleri temizle
                  </button>
                ) : null
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2.5 text-sm font-semibold text-ink-800">{title}</h2>
      {children}
    </section>
  );
}
